import { catchError, concat, Observable, of, ReplaySubject, take } from 'rxjs';
import { filter, switchMap, tap } from 'rxjs/operators';
import { ENV } from '@components/Environment.js';
import DIContainer from '@components/DependencyInjectionContainer.js';
import { isArray } from '@components/Utility/index.js';
export class CacheManager {
    ensureReady(operation) {
        return this.isReady$.pipe(filter((ready) => ready), switchMap(() => operation), take(1));
    }
    constructor(dbName) {
        this.db = null;
        this.requiredStores = new Set();
        // Create a ReplaySubject that will emit true when the DB is ready.
        this._isReadySubject = new ReplaySubject(1);
        this.isReady$ = this._isReadySubject.asObservable();
        this.dbName = dbName;
        this.dbVersion = 100;
        // IMPORTANT: Any change to the requiredStores (adding/renaming/removing) should increment the dbVersion property by +1.
        this.requiredStores.add('toolbar'); // toolbar is used for recents options and persists between logins
        this.requiredStores.add('client'); // client is used to detect new client version which clears the request cache
        this.requiredStores.add('request'); // request is for network request caching and it persists until a different user logs in or new version
        this.requiredStores.add('timebill'); // timebill is for the timebill manager and is cleared at the login form
        this.requiredStores.add('clipboard'); // clipboard is for features like copy/paste line items and is cleared at the login form
        this.requiredStores.add('session'); // session cache is for general caching purposes and is cleared at the login form
        this.requiredStores.add('persistent'); // persistent cache is for general caching purposes that does not need to be cleared on logout
        this.listenForExternalTabVersionUpdates();
        this.initializeDatabase().subscribe({
            next: () => this._isReadySubject.next(true),
            error: (err) => this._isReadySubject.error(err)
        });
    }
    listenForExternalTabVersionUpdates() {
        const broadcastManager = DIContainer.getInstance('BroadcastManager');
        broadcastManager.listen('db_version_initializing').pipe(filter((msg) => msg.payload.newVersion > this.dbVersion)).subscribe((msg) => {
            const newVersion = msg.payload.newVersion;
            console.log(`Received db_version_initializing for version ${newVersion} - closing local connection for version ${this.dbVersion}.`);
            if (this.db) {
                this.db.close();
                this.db = null;
            }
            // Mark as not ready so any indexeddb requests wont prematurely reopen the connection
            this._isReadySubject.next(false);
            this.listenForExternalTabVersionUpdateComplete(newVersion);
        });
    }
    listenForExternalTabVersionUpdateComplete(newVersion) {
        const broadcastManager = DIContainer.getInstance('BroadcastManager');
        // wait for the corresponding upgrade complete event
        broadcastManager.listen('db_version_initialized')
            .pipe(filter((msg) => msg.payload.newVersion === newVersion), take(1))
            .subscribe(() => {
            console.log(`Received db_version_initialized for version ${newVersion} - reinitializing DB at new version.`);
            this.dbVersion = newVersion;
            this.openDB(true).subscribe({
                next: () => {
                    this._isReadySubject.next(true);
                },
                error: (err) => this._isReadySubject.error(err)
            });
        });
    }
    initializeDatabase() {
        const broadcastManager = DIContainer.getInstance('BroadcastManager');
        broadcastManager.send('db_version_initializing', { payload: { newVersion: this.dbVersion } });
        return new Observable((observer) => {
            const request = indexedDB.open(this.dbName, this.dbVersion);
            request.onupgradeneeded = (event) => {
                this.db = event.target.result;
                console.log(`Upgrading DB from version ${this.db.version}`);
                this.createMissingStores();
            };
            request.onsuccess = (event) => {
                this.db = event.target.result;
                console.log('IndexedDB initialized successfully.');
                broadcastManager.send('db_version_initialized', { payload: { newVersion: this.db.version } });
                observer.next();
                observer.complete();
            };
            request.onerror = (event) => {
                console.log('IndexedDB error', event);
                observer.error(new Error('Database error: ' + event.target.error));
            };
            request.onblocked = () => {
                console.warn('Database upgrade blocked. Please close other connections (tabs) and/or refresh this tab.');
                alert('Database upgrade blocked. Please close other tabs and refresh.');
            };
        });
    }
    openDB(force) {
        const openDb$ = new Observable((observer) => {
            if (this.db) {
                observer.next();
                observer.complete();
                return;
            }
            const request = indexedDB.open(this.dbName, this.dbVersion);
            request.onsuccess = (event) => {
                this.db = event.target.result;
                observer.next();
                observer.complete();
            };
            request.onerror = (event) => {
                observer.error(new Error('Database error: ' + event.target.error));
            };
        });
        return force ? openDb$ : this.ensureReady(openDb$);
    }
    createMissingStores() {
        if (!this.db)
            return;
        for (const storeName of this.requiredStores) {
            if (!this.db.objectStoreNames.contains(storeName)) {
                this.db.createObjectStore(storeName);
            }
        }
    }
    getObjectStore(storeName, mode = 'readwrite') {
        return this.openDB().pipe(switchMap(() => {
            if (!this.db) {
                throw new Error('Database is not initialized.');
            }
            return of(this.db.transaction(storeName, mode).objectStore(storeName));
        }));
    }
    setCache(cacheName, key, value) {
        const broadcastManager = DIContainer.getInstance('BroadcastManager');
        const eventName = `cache_change:${cacheName}:${key}`;
        return this.getObjectStore(cacheName).pipe(switchMap((objectStore) => {
            return new Observable((observer) => {
                const request = objectStore.put(value, key);
                request.onsuccess = () => {
                    observer.next();
                    observer.complete();
                    // Notify observers of the new value.
                    broadcastManager.send(eventName, { payload: {} });
                };
                request.onerror = (event) => {
                    observer.error(event.target.error);
                };
            });
        }));
    }
    // set multiple key/value pairs into a cache at once in a single transaction
    setCacheBatch(cacheName, keyValuePairs) {
        const broadcastManager = DIContainer.getInstance('BroadcastManager');
        return this.getObjectStore(cacheName).pipe(switchMap((objectStore) => {
            return new Observable((observer) => {
                let errorOccurred = false;
                for (const key in keyValuePairs) {
                    if (Reflect.has(keyValuePairs, key)) {
                        const request = objectStore.put(keyValuePairs[key], key);
                        request.onerror = (event) => {
                            // Capture the first error and emit it.
                            if (!errorOccurred) {
                                errorOccurred = true;
                                observer.error(event.target.error);
                            }
                        };
                        // Broadcast the update for each key.
                        broadcastManager.send(`cache_change:${cacheName}:${key}`, { payload: {} });
                    }
                }
                objectStore.transaction.oncomplete = () => {
                    if (!errorOccurred) {
                        observer.next();
                        observer.complete();
                    }
                };
                objectStore.transaction.onerror = (event) => {
                    if (!errorOccurred) {
                        errorOccurred = true;
                        observer.error(event.target.error);
                    }
                };
            });
        }));
    }
    getCache(cacheName, key, options = {}) {
        if (ENV('disableCache') && cacheName === 'request') {
            if (Reflect.has(options, 'defaultReturnValue')) {
                return of(options.defaultReturnValue);
            }
            return new Observable(subscriber => {
                subscriber.error('Cache is disabled.');
            });
        }
        return this.getObjectStore(cacheName, 'readonly').pipe(switchMap((objectStore) => {
            return new Observable((observer) => {
                const request = objectStore.get(key);
                request.onsuccess = () => {
                    if (typeof request.result === 'undefined') {
                        if (Reflect.has(options, 'defaultReturnValue')) {
                            observer.next(options.defaultReturnValue);
                            observer.complete();
                            return;
                        }
                        observer.error('requested cache key does not exist');
                    }
                    else {
                        observer.next(request.result);
                        observer.complete();
                    }
                };
                request.onerror = (event) => {
                    if (Reflect.has(options, 'defaultReturnValue')) {
                        observer.next(options.defaultReturnValue);
                        observer.complete();
                        return;
                    }
                    observer.error(event.target.error);
                };
            });
        }), catchError((e) => {
            if (Reflect.has(options, 'defaultReturnValue')) {
                return of(options.defaultReturnValue);
            }
            throw e;
        }));
    }
    /**
     * Emits the current cache value and then listens for future changes broadcast
     * across tabs for the given cache key.
     *
     * If options.defaultReturnValue is provided and getCache returns nothing,
     * that default will be used.
     *
     * @param cacheName - The name of the cache (object store)
     * @param key - The key within the cache
     * @param options - Optional options, e.g. defaultReturnValue.
     */
    getCache$(cacheName, key, options = {}) {
        const broadcastManager = DIContainer.getInstance('BroadcastManager');
        const eventName = `cache_change:${cacheName}:${key}`;
        // Observable for the initial cache value from IndexedDB.
        const initial$ = this.getCache(cacheName, key, options);
        // Observable that listens for broadcast events with updated values.
        const broadcast$ = broadcastManager.listen(eventName).pipe(switchMap(() => this.getCache(cacheName, key, options)));
        // Concat the initial value (which should complete after one emission)
        // with the broadcast stream for future changes.
        return concat(initial$, broadcast$);
    }
    deleteCache(cacheName, key) {
        return this.openDB().pipe(switchMap(() => {
            return new Observable((observer) => {
                if (cacheName) {
                    this.getObjectStore(cacheName).subscribe({
                        next: (objectStore) => {
                            if (key) {
                                const request = objectStore.delete(key);
                                request.onsuccess = () => {
                                    observer.next();
                                    observer.complete();
                                };
                                request.onerror = (event) => {
                                    observer.error(event.target.error);
                                };
                            }
                            else {
                                const clearRequest = objectStore.clear();
                                clearRequest.onsuccess = () => {
                                    observer.next();
                                    observer.complete();
                                };
                                clearRequest.onerror = (event) => {
                                    observer.error(event.target.error);
                                };
                            }
                        },
                        error: (err) => observer.error(err),
                    });
                }
                else {
                    if (!this.db) {
                        observer.error(new Error('Database is not initialized.'));
                        return;
                    }
                    const transaction = this.db.transaction(Array.from(this.db.objectStoreNames), 'readwrite');
                    const objectStoreNames = Array.from(this.db.objectStoreNames);
                    let completedStores = 0;
                    objectStoreNames.forEach((storeName) => {
                        const objectStore = transaction.objectStore(storeName);
                        const clearRequest = objectStore.clear();
                        clearRequest.onsuccess = () => {
                            completedStores++;
                            if (completedStores === objectStoreNames.length) {
                                observer.next();
                                observer.complete();
                            }
                        };
                        clearRequest.onerror = (event) => {
                            observer.error(event.target.error);
                        };
                    });
                    transaction.onerror = (event) => {
                        observer.error(event.target.error);
                    };
                }
            });
        }));
    }
    // Delete every entry in a store whose key starts with `prefix`.
    // Emits an array of the keys that were deleted.
    deleteCacheByPrefix(cacheName, prefix) {
        // const broadcastManager = DIContainer.getInstance<BroadcastManagerInterface>('BroadcastManager');
        return this.getObjectStore(cacheName).pipe(switchMap((objectStore) => {
            return new Observable((observer) => {
                const deletedKeys = [];
                // Lexicographic prefix range: [prefix, prefix + \uFFFF)
                const upperBound = prefix + '\uFFFF';
                const range = IDBKeyRange.bound(prefix, upperBound, false, true);
                const cursorReq = objectStore.openCursor(range);
                cursorReq.onsuccess = (e) => {
                    const cursor = e.target.result;
                    if (!cursor) {
                        observer.next(deletedKeys);
                        observer.complete();
                        return;
                    }
                    const key = cursor.key;
                    if (typeof key === 'string' && key.startsWith(prefix)) {
                        const delReq = cursor.delete();
                        delReq.onsuccess = () => {
                            deletedKeys.push(key);
                            // broadcastManager.send(`cache_change:${cacheName}:${key}`, { payload: {} });
                            cursor.continue();
                        };
                        delReq.onerror = (ev) => observer.error(ev.target.error);
                    }
                    else {
                        cursor.continue();
                    }
                };
                cursorReq.onerror = (ev) => observer.error(ev.target.error);
            });
        }));
    }
    deleteCacheByPredicate(cacheName, predicate) {
        return this.getObjectStore(cacheName).pipe(switchMap((objectStore) => {
            return new Observable((observer) => {
                const deletedKeys = [];
                const cursorReq = objectStore.openCursor();
                const processCursor = async (cursor) => {
                    if (!cursor) {
                        observer.next(deletedKeys);
                        observer.complete();
                        return;
                    }
                    const key = cursor.key;
                    const value = cursor.value;
                    if (typeof key === 'string') {
                        try {
                            const shouldDelete = await predicate({
                                key,
                                value
                            });
                            if (shouldDelete) {
                                const delReq = cursor.delete();
                                delReq.onsuccess = () => {
                                    deletedKeys.push(key);
                                    cursor.continue();
                                };
                                delReq.onerror = (e) => observer.error(e.target.error);
                                return;
                            }
                        }
                        catch (err) {
                            observer.error(err);
                            return;
                        }
                    }
                    cursor.continue();
                };
                cursorReq.onsuccess = (e) => {
                    const cursor = e.target
                        .result;
                    processCursor(cursor);
                };
                cursorReq.onerror = (e) => observer.error(e.target.error);
            });
        }));
    }
    wireInvalidationRule(rule) {
        const bm = DIContainer.getInstance('BroadcastManager');
        const cm = this;
        const reg = DIContainer.getInstance('ComponentRegistry');
        rule.events.forEach((eventName) => {
            bm.listen(eventName).subscribe((broadcast) => {
                const payloadArr = isArray(broadcast.payload)
                    ? broadcast.payload
                    : [broadcast.payload];
                const scopes = rule.getScopes(payloadArr)
                    .map(s => (s || '').toLowerCase())
                    .filter(Boolean);
                // cache invalidation
                cm.deleteCacheByPredicate('request', ({ key }) => {
                    key = (key || '').toLowerCase();
                    return rule.shouldDeleteCacheKey(key, scopes);
                }).pipe(tap(() => {
                    // component refresh
                    reg.forEach((component) => {
                        if (rule.shouldRefreshComponent(component, scopes)) {
                            (rule.refreshComponent ?? ((c) => c.refreshData()))(component);
                        }
                    });
                })).subscribe();
            });
        });
    }
}
